import { Emoji } from '@components/Emoji.tsx'
import { useResource } from '@hooks/useResource.ts'
import { api } from '@api'
import type { Todo } from '@hewey/shared'
import styles from './TodoWidget.module.css'

const countPriorities = (todos: Todo[] | undefined): number | undefined => {
	if (!todos) return undefined
	return todos.filter((t) => t.priority && !t.completed).length
}

export const TodoWidget = () => {
	// Same keys as the Todo app itself, so the widget and the app share one
	// cache entry and one request.
	const personal = useResource(['todos', 'personal'], () =>
		api.todo.list('personal'),
	)
	const family = useResource(['todos', 'family'], () =>
		api.todo.list('family'),
	)

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<span className={styles.emoji}>
					<Emoji char="✅" label="Todo" />
				</span>
				<span className={styles.heading}>Todo</span>
			</header>
			<div className={styles.rows}>
				<CountRow
					label="Personal"
					loading={personal.loading && !personal.data}
					error={personal.error !== undefined}
					count={countPriorities(personal.data?.todos)}
				/>
				<CountRow
					label="Family"
					loading={family.loading && !family.data}
					error={family.error !== undefined}
					count={countPriorities(family.data?.todos)}
					muted
				/>
			</div>
		</div>
	)
}

const CountRow = ({
	label,
	loading,
	error,
	count,
	muted = false,
}: {
	label: string
	loading: boolean
	error: boolean
	count: number | undefined
	muted?: boolean
}) => (
	<div className={`${styles.row} ${muted ? styles.rowMuted : ''}`}>
		<span className={styles.rowLabel}>{label}</span>
		{loading ? (
			<span className={styles.placeholder}>…</span>
		) : error || count === undefined ? (
			<span className={styles.placeholder}>—</span>
		) : (
			<span className={styles.rowValue}>
				<span className={styles.count}>{count}</span>
				<span className={styles.unit}>
					{count === 1 ? 'priority' : 'priorities'}
				</span>
			</span>
		)}
	</div>
)
